import { useState } from 'react';
import { Link, useParams } from '@tanstack/react-router';
import { ArrowLeft, ArrowRight, BookOpen, Handshake, Megaphone, Sparkles } from 'lucide-react';
import { MarketingShell } from './MarketingShell';
import { useCms, cmsText, cmsList } from '../../api/cms';
import { SINGLE_FALLBACKS, LIST_FALLBACKS } from '../../content/fallbacks';

interface Post {
  slug: string;
  title: string;
  excerpt: string;
  date: string;
  category: string;
  readMins: string;
  body: string[];
}

/** Cast a CMS list item (fields typed `string | string[]`) to the Post shape, coercing missing/malformed fields to safe defaults — never crashes on a hand-edited row. */
function toPost(raw: Record<string, string | string[]>): Post {
  const str = (v: string | string[] | undefined, fallback = ''): string => (typeof v === 'string' ? v : fallback);
  return {
    slug: str(raw.slug),
    title: str(raw.title),
    excerpt: str(raw.excerpt),
    date: str(raw.date),
    category: str(raw.category, 'Product'),
    readMins: str(raw.readMins, '1'),
    body: Array.isArray(raw.body) ? raw.body : [],
  };
}

const CATEGORY_META: Record<string, { icon: typeof BookOpen; tint: string; chip: string }> = {
  Announcements: { icon: Megaphone, tint: 'from-sky-500/20 to-sky-500/5', chip: 'text-sky-700 dark:text-sky-300' },
  Playbook: { icon: BookOpen, tint: 'from-brand/20 to-brand/5', chip: 'text-brand dark:text-brand-dm' },
  Product: { icon: Sparkles, tint: 'from-emerald-500/20 to-emerald-500/5', chip: 'text-emerald-700 dark:text-emerald-300' },
  Partners: { icon: Handshake, tint: 'from-amber-500/20 to-amber-500/5', chip: 'text-amber-700 dark:text-amber-300' },
};

function Thumb({ category, className = '' }: { category: string; className?: string }) {
  const meta = CATEGORY_META[category] ?? CATEGORY_META.Product!;
  const Icon = meta.icon;
  return (
    <div className={`relative overflow-hidden rounded-xl bg-gradient-to-br ${meta.tint} border border-zinc-200/70 dark:border-zinc-800/70 grid place-items-center ${className}`}>
      <Icon className="w-10 h-10 text-zinc-900/30 dark:text-white/30" />
      <span className="absolute top-3 left-3 text-[11px] font-semibold uppercase tracking-wider text-zinc-700/80 dark:text-zinc-200/80">
        {category}
      </span>
    </div>
  );
}

export function Blog() {
  const { data: cms } = useCms();
  const intro = SINGLE_FALLBACKS['blog.intro']!;
  const posts = cmsList(cms, 'blog.posts', LIST_FALLBACKS['blog.posts']!).map(toPost);
  const categories = ['All', ...Array.from(new Set(posts.map((p) => p.category)))];
  const [active, setActive] = useState('All');
  const filtered = active === 'All' ? posts : posts.filter((p) => p.category === active);
  const [featured, ...rest] = filtered;

  return (
    <MarketingShell>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-14 sm:pt-20 pb-20">
        <header>
          <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50">
            {cmsText(cms, 'blog.intro', 'headline', intro.headline!)}
          </h1>
          <p className="mt-3 text-lg text-zinc-600 dark:text-zinc-300 max-w-2xl leading-relaxed">
            {cmsText(cms, 'blog.intro', 'subhead', intro.subhead!)}
          </p>
        </header>

        {/* Category filter */}
        <div className="mt-8 flex flex-wrap gap-2 border-b border-zinc-200 dark:border-zinc-800 pb-4">
          {categories.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setActive(c)}
              className={
                'px-3.5 py-1.5 rounded-full text-sm font-medium transition ' +
                (active === c
                  ? 'bg-zinc-900 text-white dark:bg-zinc-100 dark:text-zinc-900'
                  : 'text-zinc-600 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800')
              }
            >
              {c}
            </button>
          ))}
        </div>

        {/* Featured */}
        {featured && (
          <Link
            to="/blog/$slug"
            params={{ slug: featured.slug }}
            className="group mt-10 grid lg:grid-cols-2 gap-6 lg:gap-10 items-center"
          >
            <Thumb category={featured.category} className="aspect-[16/10] w-full" />
            <div>
              <div className="flex items-center gap-2 text-xs text-zinc-500 dark:text-zinc-400">
                <span className={`font-semibold uppercase tracking-wider ${CATEGORY_META[featured.category]?.chip ?? ''}`}>{featured.category}</span>
                <span aria-hidden="true">·</span>
                <span>{featured.date}</span>
                <span aria-hidden="true">·</span>
                <span className="tabular-nums">{featured.readMins} min read</span>
              </div>
              <h2 className="mt-3 text-2xl sm:text-3xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50 leading-tight group-hover:text-brand dark:group-hover:text-brand-dm transition">
                {featured.title}
              </h2>
              <p className="mt-3 text-zinc-600 dark:text-zinc-300 leading-relaxed">{featured.excerpt}</p>
              <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-brand dark:text-brand-dm">
                Read story <ArrowRight className="w-3.5 h-3.5 transition group-hover:translate-x-0.5" />
              </span>
            </div>
          </Link>
        )}

        {/* Grid */}
        {rest.length > 0 && (
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-10">
            {rest.map((p) => (
              <Link key={p.slug} to="/blog/$slug" params={{ slug: p.slug }} className="group flex flex-col">
                <Thumb category={p.category} className="aspect-[16/10] w-full" />
                <div className="mt-4 flex items-center gap-2 text-xs text-zinc-500 dark:text-zinc-400">
                  <span className={`font-semibold uppercase tracking-wider ${CATEGORY_META[p.category]?.chip ?? ''}`}>{p.category}</span>
                  <span aria-hidden="true">·</span>
                  <span>{p.date}</span>
                </div>
                <h3 className="mt-2 text-lg font-semibold text-zinc-950 dark:text-zinc-50 leading-snug group-hover:text-brand dark:group-hover:text-brand-dm transition">
                  {p.title}
                </h3>
                <p className="mt-1.5 text-sm text-zinc-600 dark:text-zinc-300 leading-relaxed">{p.excerpt}</p>
              </Link>
            ))}
          </div>
        )}
      </div>
    </MarketingShell>
  );
}

export function BlogPost() {
  const { slug } = useParams({ strict: false }) as { slug?: string };
  const { data: cms } = useCms();
  const posts = cmsList(cms, 'blog.posts', LIST_FALLBACKS['blog.posts']!).map(toPost);
  const post = posts.find((p) => p.slug === slug);
  const notFoundText = cmsText(cms, 'blog.notFound', 'text', SINGLE_FALLBACKS['blog.notFound']!.text!);

  return (
    <MarketingShell>
      <article className="max-w-2xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
        <Link to="/blog" className="inline-flex items-center gap-1.5 text-sm text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 transition">
          <ArrowLeft className="w-4 h-4" /> Newsroom
        </Link>

        {post ? (
          <>
            <div className="mt-6 flex items-center gap-2 text-xs text-zinc-500 dark:text-zinc-400">
              <span className={`font-semibold uppercase tracking-wider ${CATEGORY_META[post.category]?.chip ?? ''}`}>{post.category}</span>
              <span aria-hidden="true">·</span>
              <span>{post.date}</span>
              <span aria-hidden="true">·</span>
              <span className="tabular-nums">{post.readMins} min read</span>
            </div>
            <h1 className="mt-3 text-3xl sm:text-4xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50 leading-tight">
              {post.title}
            </h1>
            <Thumb category={post.category} className="mt-7 aspect-[16/8] w-full" />
            <div className="mt-7 space-y-4">
              {post.body.map((para, i) => (
                <p key={i} className="text-[15px] sm:text-base text-zinc-700 dark:text-zinc-200 leading-relaxed">{para}</p>
              ))}
            </div>
            <div className="mt-10 pt-6 border-t border-zinc-200 dark:border-zinc-800">
              <Link to="/signup" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium text-white bg-brand hover:bg-brand/90 transition">
                Try Possignin free <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </>
        ) : (
          <div className="mt-10 text-center text-zinc-500 dark:text-zinc-400">
            <p>{notFoundText}</p>
            <Link to="/blog" className="mt-3 inline-block text-brand dark:text-brand-dm hover:underline">Back to the newsroom</Link>
          </div>
        )}
      </article>
    </MarketingShell>
  );
}
